import * as module from "module";
import { Component, Input, OnInit } from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from "@angular/forms";
import {Observable} from "rxjs";
import {Aircraft} from "../aircraft";
import {AircraftService} from "../aircraft.service";
import {DealerAircraftService} from "../../dealer-customer-management/dealer-aircraft.service";
import {ValidationService} from "../validation.service";
import {AircraftSerialNumberFormatRequest} from "../aircraft-serial-number-format-request";

const EARLIEST_AIRCRAFT_YEAR = 1903;
const DEFAULT_AIRCRAFT_SERIAL_NUMBER_HINT = 15604356;
const VALID_TAIL_NUMBER_REGEX = module.config().validTailNumberRegex;

@Component({
	selector: "aircraft-form",
	template: `
		<div [formGroup]="aircraftFormGroup">
			<div class="adddevicesection_heading">
				<h2 *ngIf="!editMode">Add a New Aircraft</h2>
				<h2 *ngIf="editMode">Edit Aircraft</h2>
				<p><span>*</span> Required Fields</p>
			</div>

			<div class="alert alert-warning" *ngIf="aircraftRegistration.disabled">
				Your aircraft details are no longer editable.
				Please contact Aviation Product Support if you need to make changes to these details.
			</div>

			<div class="form_row">
				<div class="form_column form_column-50">

					<!---------- INPUT - AIRCRAFT REGISTRATION ---------->
					<div class="form_field">
						<label for="aircraftRegistration">Aircraft Registration (Tail Number) <span>*</span></label>
						<input type="text"
							id="aircraftRegistration"
							formControlName="aircraftRegistration"
							(input)="aircraftRegistration.setValue(aircraftRegistration.value.toUpperCase())">
						<form-errors
							[control]="aircraftRegistration"
							[messages]="{pattern: 'Must use A-Z and 0-9 only and match FAA/INTL format'}">
						</form-errors>
						<p class="form_helptext">Example: N54321</p>
					</div>

				</div>
			</div>
			<div class="form_row">
				<div class="form_column form_column-50">

					<!---------- INPUT - AIRCRAFT MAKE/MODEL/SERIES ---------->
					<div class="form_field">
						<label for="aircraftSeries">Aircraft Make / Model / Series <span>*</span></label>
						<aircraft-series-typeahead
							controlID="aircraftSeries"
							formControlName="aircraftModelId"
							(seriesChanged)="onAircraftSeriesChanged($event)">
						</aircraft-series-typeahead>
						<form-errors [control]="aircraftModelId"></form-errors>
						<p class="form_helptext">Example: CESSNA 182 P</p>
					</div>

				</div>
				<div class="form_column form_column-50">
					<div class="form_row">
						<div class="form_column form_column-70">

							<!---------- INPUT - AIRCRAFT SERIAL ---------->
							<div class="form_field form_field-leftmargin">
								<label for="aircraftSerial">Serial Number <span>*</span></label>
								<input type="text"
									   id="aircraftSerial"
									   formControlName="aircraftSerial"
								       [ngClass]="{'default-error': aircraftSerialNumberHint != null}">
								<form-errors [control]="aircraftSerial" [shouldShowErrorTextByDefault]="aircraftSerialNumberHint != null"></form-errors>
								<p class="form_helptext">
									Example: {{aircraftSerialNumberHint != null ? aircraftSerialNumberHint : defaultAircraftSerialNumberHint}}
								</p>
							</div>

						</div>
						<div class="form_column form_column-50">

							<!---------- INPUT - AIRCRAFT YEAR ---------->
							<div class="form_field form_field-leftmargin">
								<label for="aircraftYear">Year <span>*</span></label>
								<select
										id="aircraftYear"
										formControlName="aircraftYear">
									<option *ngFor="let year of allAircraftYears" [ngValue]="year">{{year}}</option>
								</select>
								<form-errors [control]="aircraftYear"></form-errors>
							</div>

						</div>
					</div>
				</div>
			</div>
			<div class="form_row">
				<div class="form_column form_column-50">

					<!---------- INPUT - AIRCRAFT NAME ---------->
					<div class="form_field">
						<label for="aircraftName">Aircraft Name <span>*</span></label>
						<input type="text"
							id="aircraftName"
							formControlName="aircraftName">
						<form-errors [control]="aircraftName"></form-errors>
						<p class="form_helptext">Give this aircraft a helpful name for your own reference.</p>
					</div>

				</div>
				<div class="form_column form_column-50">

					<!---------- INPUT - ADD TO GARMIN PILOT ---------->
					<!-- TODO: Implement adding to Garmin Pilot (Aircraft profile details)
					<div class="form_field form_field-leftmargin adddevice_garminpilotcheckbox">
						<label for="addAircraftToGarminPilot" class="form_checkboxlabel">
							<input type="checkbox"
								id="addAircraftToGarminPilot"
								formControlName="addAircraftToGarminPilot"> Add this aircraft to Garmin Pilot
						</label>
					</div>
					-->

				</div>
			</div>
		</div>
	`,
})
export class AircraftFormComponent implements OnInit {
	constructor(private validationService: ValidationService) {}

	@Input() aircraftFormGroup: FormGroup;
	@Input() aircraftSerialNumberHint: string;
	@Input() editMode = false;
	@Input() deviceModelIds: Array<number> = [];

	allAircraftYears: Array<number> = [];
	defaultAircraftSerialNumberHint: number = DEFAULT_AIRCRAFT_SERIAL_NUMBER_HINT;

	ngOnInit() {
		// Get all possible aircraft years
		let nextYear: number = new Date().getFullYear() + 1;
		this.allAircraftYears = new Array<number>();
		for (let year = nextYear; year >= EARLIEST_AIRCRAFT_YEAR; year--) {
			this.allAircraftYears.push(year);
		}
	}

	static createFormGroup(aircraft: Partial<Aircraft>, aircraftService: AircraftService, editMode: boolean, aircraftList: Array<Aircraft> = null, serialNumberRegex: RegExp = null) {
		let existingAircraft = aircraftList ? Observable.of(aircraftList) : aircraftService.getAircraft(false, false, true);

		return this.create(aircraft, existingAircraft, editMode, serialNumberRegex);
	}

	static createDealerFormGroup(aircraft: Partial<Aircraft>, customerGuid: string, dealerAircraftService: DealerAircraftService, editMode: boolean, aircraftList: Array<Aircraft> = null, serialNumberRegex: RegExp = null) {
		let existingAircraft = aircraftList ? Observable.of(aircraftList) : dealerAircraftService.getAllAircraft(customerGuid);

		return this.create(aircraft, existingAircraft, editMode, serialNumberRegex);

	}

	private static create(aircraft: Partial<Aircraft>, existingAircraft: Observable<Array<Aircraft>>, editMode: boolean, serialNumberRegex: RegExp) {
		// Fetch already-used tail numbers *once*
		let originalTailNumber = aircraft.id;
		let existingTailNumbers = existingAircraft
			.map(aircraft => new Set(aircraft.map(a => a.id).filter(x => x !== originalTailNumber)))
			.publishLast()
			.refCount();

		function validateUniqueTailNumber(control: AbstractControl) {
			return existingTailNumbers.map(existing => existing.has(control.value) ? {inuse: true} : null);
		}

		const doesAircraftHaveMasterUuid = aircraft.masterAircraftUuid && aircraft.masterAircraftUuid.length > 0;

		return new FormGroup({
			aircraftRegistration: new FormControl(
				{value: aircraft.id, disabled: doesAircraftHaveMasterUuid},
				[
					Validators.required,
					Validators.maxLength(10),
					// Validation is strict for N-numbers to match FAA format: https://www.faa.gov/licenses_certificates/aircraft_certification/aircraft_registry/forming_nnumber
					// International validation is simplified to alphanumeric starting with a character other than N
					// This is required due to Aircraft Management validation and prevents failures later in the aircraft's life
					Validators.pattern(VALID_TAIL_NUMBER_REGEX),
				],
				[validateUniqueTailNumber]),
			aircraftModelId: new FormControl(
				{value: aircraft.aircraftModelId, disabled: editMode || doesAircraftHaveMasterUuid},
				Validators.required),
			aircraftSerial: new FormControl(
				{value: aircraft.serial, disabled: doesAircraftHaveMasterUuid},
				this.getSerialNumberValidators(serialNumberRegex)
			),
			aircraftYear: new FormControl(
				{value: aircraft.year || new Date().getFullYear(), disabled: doesAircraftHaveMasterUuid},
				Validators.required),
			aircraftName: new FormControl(
				{value: aircraft.name, disabled: doesAircraftHaveMasterUuid},
				Validators.required),
		});
	}

	onAircraftSeriesChanged(value: { modelId: number, makeId: number } | null) {
		if (value) {
			this.validationService.getAircraftSerialNumberFormat(new AircraftSerialNumberFormatRequest({
				makeId: value.makeId,
				modelId: value.modelId,
				flyDeviceModelId: this.deviceModelIds
			}))
				.subscribe(response => {
					this.aircraftSerialNumberHint = response.hint;
					this.aircraftSerial.setValidators(AircraftFormComponent.getSerialNumberValidators(response.regex ? new RegExp(response.regex) : null));
				}, (error) => {
					// DON'T THROW ANY ERRORS (FINAL HARD VALIDATION PERFORMED DURING MASTER AIRCRAFT MIGRATION)
					console.log(error);
					this.aircraftSerial.setValidators(AircraftFormComponent.getSerialNumberValidators(null));
				});
		} else {
			this.aircraftSerial.setValidators(AircraftFormComponent.getSerialNumberValidators(null));
		}
	}

	private static getSerialNumberValidators(serialNumberRegex: RegExp) {
		return serialNumberRegex ? [Validators.required, Validators.pattern(serialNumberRegex)] : [Validators.required];
	}

	get aircraftRegistration() { return this.aircraftFormGroup.get("aircraftRegistration"); }
	get aircraftModelId() { return this.aircraftFormGroup.get("aircraftModelId"); }
	get aircraftSerial() { return this.aircraftFormGroup.get("aircraftSerial"); }
	get aircraftYear() { return this.aircraftFormGroup.get("aircraftYear"); }
	get aircraftName() { return this.aircraftFormGroup.get("aircraftName"); }
}
